import branditRails from '../../../../api/brandit_rails'
import {
  setCheckoutSessionTokenStatusAction,
  setCheckoutSessionTokenErrorsAction,
  clearCheckoutSessionTokenErrorsAction,
  setTokenDataAction,
  setMultiAddressSelectionAction,
  setSelectedBillingAddressAction,
  setSelectedCustomerAddressIdAction,
  setGuestCheckoutDataAction
} from './checkoutSessionToken.actions'

/** Extract and normalize checkout session data from API response */
const parseTokenResponse = data => {
  const checkoutSessionData = data.checkout_session_data
  const multiAddressSelection = checkoutSessionData?.multi_address_selection || []
  const selectedBillingAddress = checkoutSessionData?.selected_billing_address || null
  const selectedCustomerAddressId = checkoutSessionData?.selected_customer_address_id || null
  const guestCheckoutData = checkoutSessionData?.guest_checkout_data || null
  const useShippingAsBilling = checkoutSessionData?.use_shipping_as_billing ?? guestCheckoutData?.use_shipping_as_billing

  return {
    token: data.token,
    shortCode: data.short_code,
    multiAddressSelection,
    selectedBillingAddress,
    selectedCustomerAddressId,
    guestCheckoutData,
    useShippingAsBilling,
    paymentStatus: data.payment_status,
    used: data.used
  }
}

/** POST /checkout_session_tokens - Create or get existing token */
export const createCheckoutSessionToken = () => async dispatch => {
  try {
    dispatch(setCheckoutSessionTokenStatusAction('loading'))
    dispatch(clearCheckoutSessionTokenErrorsAction())

    const { data } = await branditRails.post('/checkout_session_tokens')
    const parsed = parseTokenResponse(data)

    dispatch(setTokenDataAction({ ...parsed, status: 'succeeded' }))

    return {
      token: parsed.token,
      shortCode: parsed.shortCode,
      multiAddressSelection: parsed.multiAddressSelection,
      selectedBillingAddress: parsed.selectedBillingAddress,
      selectedCustomerAddressId: parsed.selectedCustomerAddressId,
      guestCheckoutData: parsed.guestCheckoutData,
      useShippingAsBilling: parsed.useShippingAsBilling
    }
  } catch (err) {
    dispatch(setCheckoutSessionTokenStatusAction('failed'))
    dispatch(
      setCheckoutSessionTokenErrorsAction({
        action: 'createCheckoutSessionToken',
        message: err.response?.data?.error || err.message || 'Failed to create checkout session token'
      })
    )
    return null
  }
}

/** GET /checkout_session_tokens/:token - Fetch token state for restore on page refresh */
export const fetchCheckoutSessionToken = token => async dispatch => {
  if (!token) return null

  try {
    dispatch(setCheckoutSessionTokenStatusAction('loading'))
    dispatch(clearCheckoutSessionTokenErrorsAction())

    const { data } = await branditRails.get(`/checkout_session_tokens/${token}`)
    const parsed = parseTokenResponse(data)

    dispatch(setTokenDataAction({ ...parsed, status: 'succeeded' }))

    return {
      ...data,
      multiAddressSelection: parsed.multiAddressSelection,
      selectedBillingAddress: parsed.selectedBillingAddress,
      selectedCustomerAddressId: parsed.selectedCustomerAddressId,
      guestCheckoutData: parsed.guestCheckoutData,
      useShippingAsBilling: parsed.useShippingAsBilling
    }
  } catch (err) {
    if (err.response?.status === 404) {
      dispatch(setCheckoutSessionTokenStatusAction('init'))
      return null
    }

    dispatch(setCheckoutSessionTokenStatusAction('failed'))
    dispatch(
      setCheckoutSessionTokenErrorsAction({
        action: 'fetchCheckoutSessionToken',
        message: err.response?.data?.error || err.message || 'Failed to fetch checkout session token'
      })
    )
    return null
  }
}

/** PATCH /checkout_session_tokens/:token - Update checkout session data */
export const updateCheckoutSessionToken =
  (token, { multiAddressSelection, selectedBillingAddress, selectedCustomerAddressId, useShippingAsBilling, guestCheckoutData } = {}) =>
  async dispatch => {
    if (!token) return false

    try {
      dispatch(clearCheckoutSessionTokenErrorsAction())

      const checkoutSessionDataPayload = {}
      if (multiAddressSelection !== undefined) {
        checkoutSessionDataPayload.multi_address_selection = multiAddressSelection
      }
      if (selectedBillingAddress !== undefined) {
        checkoutSessionDataPayload.selected_billing_address = selectedBillingAddress
      }
      if (selectedCustomerAddressId !== undefined) {
        checkoutSessionDataPayload.selected_customer_address_id = selectedCustomerAddressId
      }
      if (useShippingAsBilling !== undefined) {
        checkoutSessionDataPayload.use_shipping_as_billing = useShippingAsBilling
      }
      if (guestCheckoutData !== undefined) {
        checkoutSessionDataPayload.guest_checkout_data = guestCheckoutData
      }

      const { data } = await branditRails.patch(`/checkout_session_tokens/${token}`, {
        checkout_session_data: checkoutSessionDataPayload
      })

      const responseData = data.checkout_session_data
      if (responseData?.multi_address_selection) {
        dispatch(setMultiAddressSelectionAction(responseData.multi_address_selection))
      }
      if (responseData?.selected_billing_address) {
        dispatch(setSelectedBillingAddressAction(responseData.selected_billing_address))
      }
      if (responseData?.selected_customer_address_id) {
        dispatch(setSelectedCustomerAddressIdAction(responseData.selected_customer_address_id))
      }
      if (responseData?.guest_checkout_data) {
        dispatch(setGuestCheckoutDataAction(responseData.guest_checkout_data))
      }

      return true
    } catch (err) {
      if (err.response?.status === 422 || err.response?.status === 404) {
        return false
      }

      dispatch(
        setCheckoutSessionTokenErrorsAction({
          action: 'updateCheckoutSessionToken',
          message: err.response?.data?.error || err.message || 'Failed to update checkout session'
        })
      )
      return false
    }
  }

/** Legacy wrapper for multi-address only updates */
export const updateCheckoutSessionTokenMultiAddress = (token, multiAddressSelection) => async dispatch => {
  return dispatch(updateCheckoutSessionToken(token, { multiAddressSelection }))
}

let debounceTimer = null

/** Debounced version of updateCheckoutSessionTokenMultiAddress (500ms default) */
export const debouncedUpdateCheckoutSessionTokenMultiAddress =
  (token, multiAddressSelection, delay = 500) =>
  dispatch => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      dispatch(updateCheckoutSessionTokenMultiAddress(token, multiAddressSelection))
      debounceTimer = null
    }, delay)
  }
